import { estadoPorId, podeConfirmar } from '@/agenda/confirmacao';

/**
 * A caixinha de um agendamento na grade (VF-3).
 *
 * ★ DOIS ALVOS, E SÓ DOIS: o corpo abre o detalhe, o flag confirma. Nada mais
 * compete pelo toque.
 *
 * ⚠ O flag é uma ESCRITA A UM TOQUE num alvo pequeno, ao lado de outras
 * caixinhas. O que impede que toque errado vire gravação errada, sem diálogo de
 * confirmação (que mataria a razão de ser do flag):
 *
 *   1. desmarcar é O MESMO ALVO — o erro se corrige com um segundo toque no
 *      mesmo lugar. Esta é a resposta principal;
 *   2. área de toque de 44px, separada do corpo;
 *   3. `stopPropagation` no clique E no teclado: o corpo inteiro da caixinha é
 *      clicável (o alvo precisa ser maior que o texto), então sem isso o flag
 *      abriria o detalhe junto;
 *   4. o flag só EXISTE em consulta agendada — não há como tocá-lo por engano
 *      numa consulta já resolvida;
 *   5. a escrita não move funil, não cria tarefa e não entra em dinheiro.
 */
export default function CaixaAgendamento({ caixa, aoAbrir, aoAlternar, ocupado = false }) {
  const estado = estadoPorId(caixa.estado);
  const confirmado = caixa.estado === 'confirmado';
  const nome = caixa.nome || 'Sem nome';

  // Rótulo completo SEMPRE, mesmo quando só cabe o glifo na tela: cor e forma
  // não chegam a quem usa leitor de tela.
  const descricao = [
    nome,
    caixa.hora,
    estado.label.toLowerCase(),
    caixa.retorno ? 'retorno' : 'avaliação',
    caixa.foraDaGrade ? 'fora da grade' : null,
    // ★ A-3(a) — o SDR entra na descrição, não só no visual: cor e posição não
    // chegam a quem usa leitor de tela, e o nome do responsável é informação.
    // ★★ Ajuste 4: aqui vai o nome COMPLETO. O encurtamento existe para caber na
    // coluna; ⚠ leitor de tela e `title` não têm limite de largura, e "Ana P."
    // informa menos que "Ana Pereira" quando o espaço não é o problema.
    caixa.sdr ? `SDR ${caixa.sdrCompleto || caixa.sdr}` : null,
  ]
    .filter(Boolean)
    .join(', ');

  const abrir = () => aoAbrir?.(caixa);

  return (
    <div
      className={`agenda-caixa agenda-caixa--${caixa.estado}${caixa.retorno ? ' agenda-caixa--retorno' : ''}`}
      style={{ '--cor-estado': estado.cor }}
      role="button"
      tabIndex={0}
      /**
       * ★★★ A-3 duas linhas (12/out) — O `title`: nome completo + SDR + **ESTADO**.
       *
       * ★ O dono pediu *"passando o mouse: nome completo + SDR"*. ⚠⚠ Mas o estado
       * **não pode sair daqui**, e isto é garantia antiga, não preferência minha:
       * `caixa.render.test.js` ("cor nunca é o único sinal") exige o estado em
       * PALAVRA no `title`, porque ★ **verde e azul — confirmado e compareceu —
       * são o par que a deuteranopia mais confunde**, e o `title` é o recurso de
       * quem VÊ a tela e não distingue as cores.
       *
       * ★★★ Então o tooltip leva os três, e ninguém perde nada:
       *     `Neuza Bastos Bezerra — Jessica · confirmado`
       *
       * ★ O `aria-label` segue com a descrição INTEIRA (hora, tipo, fora da
       * grade): leitor de tela não tem problema de largura.
       */
      title={[
        nome,
        caixa.sdrCompleto ? ` — ${caixa.sdrCompleto}` : '',
        ` · ${estado.label.toLowerCase()}`,
      ].join('')}
      aria-label={descricao}
      onClick={abrir}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          abrir();
        }
      }}
    >
      <span className="agenda-caixa-glifo" aria-hidden="true">
        {estado.glifo}
      </span>
      {/**
        * ★★★ A-3 DUAS LINHAS (12/out/2026) — desenho aprovado em
        * `docs/a3-desenho-duas-linhas.html`.
        *
        * ★ 1ª linha o PACIENTE, 2ª linha a SDR (menor, cinza). ⚠ O corte com "…"
        * passa a valer por LINHA, e é isso que para de competir pela mesma
        * largura — era o defeito que o dono viu no ar.
        *
        * ★★★ ESTE ENVOLTÓRIO NÃO MUDA A VISÃO DE DIA, e isso é de propósito: o
        * CSS lhe dá `display: contents` fora da semana, o que faz os filhos
        * voltarem a ser itens diretos do flex — **o dia fica exatamente como
        * está hoje**, numa linha. Só `--compacta` o transforma em coluna.
        * ⚠ Sem isso eu teria de manter dois JSX, e dois desenhos divergem.
        *
        * ★ Sem SDR (o caso da RECEPÇÃO, que não carrega leads) sobra só a 1ª
        * linha — o que o dono pediu, e sai de graça.
        */}
      <span className="agenda-caixa-texto">
        <span className="agenda-caixa-nome">{nome}</span>
        {caixa.sdr && (
          <span className="agenda-caixa-sdr" aria-hidden="true">
            {caixa.sdr}
          </span>
        )}
      </span>
      {caixa.retorno && (
        <span className="agenda-caixa-tag" aria-hidden="true" title="Retorno — não ocupa vaga">
          R
        </span>
      )}
      <span className="agenda-caixa-estado" aria-hidden="true">
        {estado.label}
      </span>

      {podeConfirmar(caixa) && (
        <button
          type="button"
          role="switch"
          aria-checked={confirmado}
          className="agenda-flag"
          disabled={ocupado}
          aria-label={`Confirmado — ${nome}, ${caixa.hora}`}
          onClick={(e) => {
            e.stopPropagation();
            aoAlternar?.(caixa, !confirmado);
          }}
          onKeyDown={(e) => e.stopPropagation()}
        >
          <span aria-hidden="true">✓</span>
        </button>
      )}
    </div>
  );
}
